import { tx as translateInterfaceText } from '@/utils/i18n';
import { z } from 'zod';
import { localizedZodErrorMap } from '@/utils/i18n/zodErrorMap';
import { COLOR_PATTERN, ID_PATTERNS, LIMITS, STRUCTURE } from './codes';
import { normalizeExitUrl } from './url';

// Tempos em segundos com passo de 0,1 (1.1 ok, 1.05 não).
const isTenthStep = (n: number) => Math.abs(Math.round(n * 10) - n * 10) < 1e-6;

const time = z
  .number()
  .finite()
  .min(0)
  .max(LIMITS.TIME_MAX)
  .refine(isTenthStep, () => ({ message: translateInterfaceText("Use tempos com passo de 0,1 s") }));

const color = z.string({ errorMap: localizedZodErrorMap({ invalid_string: "Use uma cor no formato #RRGGBB" }) }).regex(COLOR_PATTERN);

const nodeId = z.string({ errorMap: localizedZodErrorMap({ invalid_string: "Id de etapa inválido" }) }).regex(ID_PATTERNS.node);
const choiceId = z.string({ errorMap: localizedZodErrorMap({ invalid_string: "Id de opção inválido" }) }).regex(ID_PATTERNS.choice);
const exitId = z.string({ errorMap: localizedZodErrorMap({ invalid_string: "Id de saída inválido" }) }).regex(ID_PATTERNS.exit);

const choiceActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('go_to_node'), target_node_id: nodeId }).strict(),
  z.object({ type: z.literal('exit'), exit_id: exitId }).strict(),
]);

const choiceSchema = z
  .object({
    id: choiceId,
    label: z.string().max(LIMITS.CHOICE_LABEL_MAX),
    action: choiceActionSchema.nullable(),
  })
  .strict();

const nodeSchema = z
  .object({
    id: nodeId,
    label: z.string().max(LIMITS.LABEL_MAX),
    video_id: z.string().min(1).max(64).nullable(),
    prompt: z.string().max(LIMITS.PROMPT_MAX),
    replay_enabled: z.boolean().optional(),
    timing: z
      .object({
        show_choices_at: time.nullable(),
        pause_at: time.nullable(),
      })
      .strict(),
    choices: z.array(choiceSchema).max(LIMITS.MAX_CHOICES),
  })
  .strict();

const exitUrl = z.string().transform((value, ctx) => {
  const result = normalizeExitUrl(value);
  if (result.ok === false) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      params: { code: STRUCTURE.INVALID_URL },
      message: translateInterfaceText("Endereço inválido: use uma URL http:// ou https://"),
    });
    return z.NEVER;
  }
  return result.value;
});

const extraParamsSchema = z
  .record(
    z.string({ errorMap: localizedZodErrorMap({ invalid_string: "Nome de parâmetro inválido" }) }).regex(/^[A-Za-z0-9_.-]{1,64}$/),
    z.string().max(LIMITS.EXTRA_VALUE_MAX),
  )
  .refine((obj) => Object.keys(obj).length <= LIMITS.EXTRA_PARAMS_MAX, () => ({
    params: { code: STRUCTURE.LIMIT_EXCEEDED },
    message: translateInterfaceText("No máximo {value0} parâmetros fixos", { value0: (LIMITS.EXTRA_PARAMS_MAX) }),
  }));

const exitSchema = z
  .object({
    id: exitId,
    label: z.string().max(LIMITS.LABEL_MAX),
    url: exitUrl,
    target: z.enum(['same_tab', 'new_tab']),
    forward_params: z.boolean(),
    extra_params: extraParamsSchema,
    is_checkout: z.boolean(),
  })
  .strict();

export const graphSchema = z
  .object({
    start_node_id: nodeId.nullable(),
    nodes: z.array(nodeSchema).max(LIMITS.MAX_NODES),
    exits: z.array(exitSchema).max(LIMITS.MAX_EXITS),
  })
  .strict();

export const themeSchema = z
  .object({
    aspect_ratio: z.enum(['auto', '16:9', '9:16', '1:1', '4:5']),
    accent_color: color,
    prompt_color: color,
    choice_style: z
      .object({
        bg_color: color,
        text_color: color,
        radius: z.number().int().min(0).max(40),
        show_letter: z.boolean(),
        position: z.enum(['bottom', 'center']),
      })
      .strict(),
    scrim_opacity: z.number().min(0).max(0.9),
    border: z
      .object({
        enabled: z.boolean(),
        color,
        width: z.number().int().min(0).max(20),
        radius: z.number().int().min(0).max(40),
      })
      .strict(),
    progress: z.object({ type: z.enum(['none', 'fake', 'real']), color }).strict(),
    controls: z.enum(['none', 'minimal']),
    start: z
      .object({
        mode: z.enum(['muted_preview', 'click_to_play']),
        text: z.string().max(LIMITS.START_TEXT_MAX),
        text_color: color,
        bg_color: color,
      })
      .strict(),
  })
  .strict();

export const draftContentSchema = z.object({ graph: graphSchema, theme: themeSchema }).strict();

export const flowNameSchema = z.string().trim().min(1).max(LIMITS.NAME_MAX);
